import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { arenaApi } from "./data/api";
import type { ArenaConfig, DatasetItem, DatasetPage } from "./data/types";
import { AudioPlayer } from "./AudioPlayer";

const PAGE = 24;
const pct = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);

function nwerClass(v: number | null): string {
  if (v == null) return "stt-muted";
  if (v <= 0.1) return "stt-good";
  if (v <= 0.3) return "stt-ok";
  return "stt-bad";
}

export function DataBrowser({ config }: { config: ArenaConfig | null }) {
  const [accent, setAccent] = useState("");
  const [condition, setCondition] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<DatasetPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    arenaApi
      .dataset({ limit: PAGE, offset, accent: accent || undefined, condition: condition || undefined })
      .then(setPage)
      .catch((e) => setError(String(e.message ?? e)))
      .finally(() => setLoading(false));
  }, [accent, condition, offset]);

  const resetTo = (fn: () => void) => {
    setOffset(0);
    fn();
  };

  return (
    <div className="stt-browser">
      <div className="stt-filters">
        <select
          className="stt-select"
          value={accent}
          onChange={(e) => resetTo(() => setAccent(e.target.value))}
        >
          <option value="">All accents</option>
          {config?.accents
            .filter((a) => ["american", "british", "indian", "arabic"].includes(a.value))
            .map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
        </select>
        <select
          className="stt-select"
          value={condition}
          onChange={(e) => resetTo(() => setCondition(e.target.value))}
        >
          <option value="">Clean + noisy</option>
          <option value="clean">Clean only</option>
          <option value="noisy">Noisy only</option>
        </select>
        {page ? (
          <span className="stt-result-count">
            {page.total.toLocaleString()} samples
          </span>
        ) : null}
        <div className="stt-pager">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Prev
          </button>
          <span>
            {page ? `${offset + 1}–${Math.min(offset + PAGE, page.total)}` : "…"}
          </span>
          <button
            disabled={!page || offset + PAGE >= page.total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      </div>

      {error ? (
        <div className="stt-empty stt-empty--err">{error}</div>
      ) : loading ? (
        <div className="stt-loading">
          <Loader2 className="stt-spin" size={18} /> Loading samples…
        </div>
      ) : (
        <div className="stt-sample-list">
          {page?.items.map((item) => (
            <SampleRow
              key={item.id}
              item={item}
              open={!!open[item.id]}
              onToggle={() => setOpen((o) => ({ ...o, [item.id]: !o[item.id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SampleRow({
  item,
  open,
  onToggle,
}: {
  item: DatasetItem;
  open: boolean;
  onToggle: () => void;
}) {
  const sorted = [...item.hypotheses].sort((a, b) => (a.nwer ?? 9) - (b.nwer ?? 9));
  return (
    <div className="stt-sample-card">
      <div className="stt-sample-head">
        <div className="stt-sample-badges">
          {item.accent ? <span className="stt-badge">{item.accent}</span> : null}
          <span className={`stt-badge ${item.condition === "clean" ? "stt-badge--clean" : "stt-badge--noisy"}`}>
            {item.condition === "clean" ? "clean" : `noisy${item.snr_db ? ` · ${item.snr_db}dB` : ""}`}
          </span>
          {item.set ? <span className="stt-badge stt-badge--ghost">{item.set}</span> : null}
        </div>
        {item.audio_uri ? <AudioPlayer uri={item.audio_uri} compact /> : <span className="stt-muted">no audio</span>}
      </div>

      <div className="stt-reference">
        <span className="stt-ref-label">REF</span>
        <span className="stt-ref-text">{item.reference || "—"}</span>
      </div>

      <button className="stt-disclosure" onClick={onToggle}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {item.hypotheses.length} model transcripts
      </button>

      {open ? (
        <div className="stt-hyp-list">
          {sorted.map((h) => (
            <div className="stt-hyp" key={`${h.provider}:${h.model}`}>
              <div className="stt-hyp-meta">
                <span className="stt-hyp-prov">
                  {h.provider}
                  <span className="stt-model"> {h.model}</span>
                </span>
                <span className={`stt-hyp-nwer ${nwerClass(h.nwer)}`}>nWER {pct(h.nwer)}</span>
              </div>
              <div className="stt-hyp-text">
                {h.error ? <span className="stt-bad">error: {h.error}</span> : h.hypothesis || <span className="stt-muted">(empty)</span>}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
